import type { FeatureFlag } from "EntityTypes";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "@reduxjs/toolkit";
import type { RootState } from "StoreTypes";
import { setCurrentFacility } from "ui-modules/facility-control/facilityControlSlice";
import type { FacilityNormalized } from "EntityTypes";
import { getSessionStorageValue } from "utils/hooks/localStorage";
import { getUserFacilitiesQuery } from "./facilityQueries";
import type { UseDataInfo } from "QueryTypes";
import { useRequest } from "redux-query-react";
import { useEffect } from "react";

export const useCurrentFacilityFeatureFlags = (): FeatureFlag | undefined => {
  const currentFacilityId = useGetCurrentLocalFacilityId();
  return useSelector((state: RootState): FeatureFlag | undefined => {
    return state.entities.facilities?.[currentFacilityId]?.featureFlags;
  });
};

export const getCurrentSelectedFacilityId = (state: RootState) => {
  const currentLocalFacilityId = getCurrentLocalFacilityId();
  if (state.facility.currentFacilityId) return state.facility.currentFacilityId;
  return currentLocalFacilityId;
};

export const getCurrentLocalFacilityId = (): string => {
  return getSessionStorageValue("facilityId") ?? "";
};

/** Keep a saved facility when the user still has it; otherwise the first facility. */
export const resolveSelectedFacilityId = (
  currentFacilityId: string,
  facilityIds: readonly string[],
): string => {
  if (currentFacilityId && facilityIds.includes(currentFacilityId)) {
    return currentFacilityId;
  }
  return facilityIds[0] ?? "";
};

export const useGetCurrentLocalFacilityId = (): string =>
  getCurrentLocalFacilityId();

export const useSetCurrentLocalFacilityId = (
  facilityId: string,
  enabled: boolean,
) => {
  const dispatch = useDispatch();
  useEffect(() => {
    if (enabled) {
      dispatch(setCurrentFacility({ currentFacility: facilityId }));
    }
  }, [dispatch, facilityId, enabled]);
};

// Memoized selector to prevent unnecessary re-renders. The `facilities` cache is
// global — merged across users' queries plus single-facility fetches and
// websocket updates — so it's scoped back to one user via the `facilitiesByUser`
// index the getUserFacilities query records. Falls back to the full cache only
// when that index hasn't been populated yet (e.g. before the query resolves).
export const selectUserFacilities = createSelector(
  [
    (state: RootState) => state.entities?.facilities,
    (state: RootState, userId: string) =>
      state.entities?.facilitiesByUser?.[userId],
  ],
  (facilities, ownedIds): FacilityNormalized[] => {
    if (!facilities) return [];
    if (!ownedIds) return Object.values(facilities);
    return ownedIds
      .map((id) => facilities[id])
      .filter((f): f is FacilityNormalized => f != null);
  },
);
export const useFacilitiesByUserId = (
  userId: string,
): UseDataInfo<FacilityNormalized[]> => {
  const [queryState, refreshFacilities] = useRequest(
    getUserFacilitiesQuery(userId),
  );

  const userFacilities = useSelector((state: RootState) =>
    selectUserFacilities(state, userId),
  );

  return [userFacilities, queryState, refreshFacilities];
};
